// The contacts builder's criteria, both directions
// (`client-contact-search.page.component.ng1.ts:615` and its `init*` twins).
//
// ⚠️ IT IS NOT THE CANDIDATE SHAPE WITH FEWER KEYS. Every criterion is wrapped in its own
// object — `titleCriteria.titles`, `industryCriteria.industries`,
// `companyNamesCriteria.companyNames` — where the candidate DTO puts bare arrays at the top
// level, and the location key is `locationCriteria`, not `location`. The contacts edit screen
// rendered its sections from the alert's criteria and collected nothing, so none of this had
// been written; Save sent nothing at all.
//
// ⚠️ AND AN EMPTY CRITERION IS DELETED, NOT SENT EMPTY. Upstream's final loop drops every key
// whose value `isEmpty` — an empty array, an object with no keys, null or undefined — so a
// form with one filter sends one key.
import type { Tag } from '../common/TagsInput.tsx'
import type { KeywordCondition } from './KeywordSearchSection.tsx'
import type { AtsFilterRow } from './atsFilterData.ts'
import type { LocationCriteria, LocationRegion } from './LocationSection.tsx'
import { mapLocation, type WireLocationCriteria } from './criteriaSave.ts'
import { mapKeywords } from './criteriaMap.ts'
import { isEmptyCriterion } from './criteriaEmptiness.ts'
import { emptyClientContactKeywordCondition, LATEST_CLIENT_CONTACT_CRITERIA_VERSION } from './criteriaLoad.ts'
import type { ContactTitleRow } from './ContactFiltersSection.tsx'

export interface ClientContactFormCriteria {
  titleRows: ContactTitleRow[]
  switchedCompany: boolean
  switchedCompanyMin: string
  switchedCompanyMax: string
  previousEngagement: Tag[]
  highHiringLikelihood: boolean
  highSupply: boolean
  industries: Tag[]
  companyNames: Tag[]
  /** `sizeCriteria.sizes` / `typeCriteria.types` — both `jobCompanyInfoEnabled` only. */
  companySizes: Tag[]
  companyTypes: Tag[]
  /** `categoriesCriteria.categories`, `openJobsCriteria.min`, `lastOpenedJobCriteria.daysAgo` —
   *  the rest of the high-hiring-likelihood block, all three gated the same way. */
  hiringCategories: Tag[]
  openJobsMin?: number
  lastOpenedJobDaysAgo?: number
  location: LocationCriteria
  atsFilters: AtsFilterRow[]
  keywords: KeywordCondition[]
  versionId?: unknown
}

export const EMPTY_CLIENT_CONTACT_CRITERIA: ClientContactFormCriteria = {
  titleRows: [{}],
  switchedCompany: false,
  switchedCompanyMin: '',
  switchedCompanyMax: '',
  previousEngagement: [],
  highHiringLikelihood: false,
  highSupply: false,
  industries: [],
  companyNames: [],
  companySizes: [],
  companyTypes: [],
  hiringCategories: [],
  location: {},
  atsFilters: [{}],
  // The CONTACT default (category 0) — see `emptyClientContactKeywordCondition`.
  keywords: [emptyClientContactKeywordCondition()],
}

interface SavedTagList {
  titles?: string[] | null
  industries?: string[] | null
  companyNames?: string[] | null
}

/** `init*Criteria` — the alert's criteria as the form holds them. */
/**
 * `IEntityCriteriaTitleExtraInfo` — what the alert CONTAINER carries beside its criteria.
 *
 * ⚠️ THE CRITERIA STORE TITLES AS BARE STRINGS, so everything that makes a title a CLASS is
 * lost on reload unless this is read. `getTitlesTempCriteria` looks each saved title up here
 * by lower-cased text and rebuilds `titleClass` / `titlesAmount` / `topTitlesInClass` —
 * without it a reopened alert shows "Developer" where production shows "Developer (5 titles)",
 * and the suggestions block has no class to work from.
 */
export interface TitleExtraInfo {
  title: string
  hasTitleClass?: boolean
  titleClass?: boolean
  titlesAmount?: number
  topTitlesInClass?: string[]
}

export function loadClientContactCriteria(
  saved: Readonly<Record<string, unknown>> | undefined,
  loadLocation: (value: unknown) => LocationCriteria,
  loadAtsFilters: (value: unknown) => AtsFilterRow[],
  loadKeywords: (value: unknown) => KeywordCondition[],
  /**
   * ⚠️ THE FLAG DECIDES WHICH LOCATION KEY IS EVEN READ. `initLocationCriteria` is applied to
   * `companyLocationCriteria` when it is on and to `locationCriteria` when it is off
   * (`client-contact-search.page.component.ng1.ts:594` and `:599`) — never both. The port
   * hardcoded the off branch under a comment claiming the view model does not carry the
   * capability; it does, as `featureFlags.JOB_COMPANY_INFO`.
   */
  jobCompanyInfoEnabled = false,
  titlesExtraInfo: readonly TitleExtraInfo[] = [],
): ClientContactFormCriteria {
  // `getTitlesTempCriteria` — matched on lower-cased text, as upstream does.
  const titleTag = (text: string): Tag => {
    const info = titlesExtraInfo.find((entry) => entry.title.toLowerCase() === text.toLowerCase())
    return info === undefined
      ? { text }
      : {
          text,
          source: {
            hasTitleClass: info.hasTitleClass === true,
            titleClass: info.titleClass === true,
            titlesAmount: info.titleClass === true ? (info.titlesAmount ?? 1) : 1,
            topTitlesInClass: info.topTitlesInClass ?? [],
          },
        }
  }
  const criteria = saved ?? {}
  const get = <T,>(key: string): T | undefined => criteria[key] as T | undefined

  const titleCriteria = get<{ titles?: Array<SavedTagList> | null }>('titleCriteria')
  // ⚠️ ALWAYS AT LEAST ONE ROW. The section repeats `titleCriteria.titles` and upstream seeds
  // an empty entry, so an alert with no titles still shows one field rather than none.
  const titleRows: ContactTitleRow[] =
    titleCriteria?.titles === undefined || titleCriteria.titles === null || titleCriteria.titles.length === 0
      ? [{}]
      : titleCriteria.titles.map((row) => ({ titles: (row.titles ?? []).map(titleTag) }))

  const switched = get<{ min?: number | null; max?: number | null }>('switchedCompanyCriteria')
  const engagement = get<{ values?: Array<{ text?: string }> | null }>('highestActivityLevelCriteria')
  const hiring = get<{ highLikelihood?: boolean }>('hiringLikelihoodCriteria')
  const supply = get<{ highSupply?: boolean }>('supplyCriteria')
  const industry = get<{ industries?: string[] | null }>('industryCriteria')
  const companies = get<{ companyNames?: string[] | null }>('companyNamesCriteria')

  return {
    titleRows,
    // The criterion EXISTING is what ticks the box; `min`/`max` may both be null inside it.
    switchedCompany: switched !== undefined && switched !== null,
    switchedCompanyMin: switched?.min == null ? '' : String(switched.min),
    switchedCompanyMax: switched?.max == null ? '' : String(switched.max),
    previousEngagement: (engagement?.values ?? []).map((value) => ({ text: value.text ?? '', source: { ...value } })),
    highHiringLikelihood: hiring?.highLikelihood === true,
    highSupply: supply?.highSupply === true,
    industries: (industry?.industries ?? []).map((text) => ({ text })),
    companyNames: (companies?.companyNames ?? []).map((text) => ({ text })),
    location: loadLocation(criteria[jobCompanyInfoEnabled ? 'companyLocationCriteria' : 'locationCriteria']),
    companySizes: ((get<{ sizes?: string[] }>('sizeCriteria')?.sizes ?? []) as string[]).map((text) => ({ text })),
    companyTypes: ((get<{ types?: string[] }>('typeCriteria')?.types ?? []) as string[]).map((text) => ({ text })),
    hiringCategories: ((get<{ categories?: string[] }>('categoriesCriteria')?.categories ?? []) as string[]).map((text) => ({ text })),
    ...(get<{ min?: number }>('openJobsCriteria')?.min === undefined ? {} : { openJobsMin: get<{ min?: number }>('openJobsCriteria')?.min }),
    ...(get<{ daysAgo?: number }>('lastOpenedJobCriteria')?.daysAgo === undefined
      ? {}
      : { lastOpenedJobDaysAgo: get<{ daysAgo?: number }>('lastOpenedJobCriteria')?.daysAgo }),
    atsFilters: loadAtsFilters(criteria['atsFilters']),
    keywords: loadKeywords(criteria['keywordsCriteria']),
    // ⚠️ A NEW ALERT GETS THE LATEST VERSION, not an absent key. `getNewEmptyClientContactCriteria()`
    // stamps `versionId: 1` — the CONTACT number, which is not the candidate's 4 — and the
    // service's migration map is keyed on it. Seeding from an alert that has none (a brand new
    // one) therefore has to supply the default rather than pass `undefined` through.
    versionId: criteria['versionId'] ?? LATEST_CLIENT_CONTACT_CRITERIA_VERSION,
  }
}

/** `mapTempCriteriaToExtensionCriteria`, with its trailing delete-the-empties loop. */
export function mapClientContactCriteria(
  form: ClientContactFormCriteria,
  regions: readonly LocationRegion[] = [],
  validAtsFilters?: unknown,
  /**
   * ⚠️ THE ALERT'S OWN CRITERIA, SO A PARTIAL PORT CANNOT DELETE WHAT IT CANNOT DRAW.
   *
   * Six of this builder's criteria sit behind `jobCompanyInfoEnabled` and are not ported:
   * `categoriesCriteria`, `typeCriteria`, `sizeCriteria`, `supplyCriteria`, `openJobsCriteria`
   * and `lastOpenedJobCriteria`, plus `companyLocationCriteria`. Upstream can rebuild the
   * whole object from its form because its form holds all of them; this one would send a
   * criteria set with those keys simply ABSENT, and the server stores what it is sent — so
   * saving a rename on an alert that used them would silently delete them.
   *
   * Measured the hard way: the first live save on this screen rewrote an alert's criteria to
   * the three keys this form models.
   */
  saved?: Readonly<Record<string, unknown>>,
  jobCompanyInfoEnabled = false,
): Record<string, unknown> {
  const validTitleRows = form.titleRows.filter((row) => (row.titles?.length ?? 0) > 0)

  const mapped: Record<string, unknown> = {
    titleCriteria:
      validTitleRows.length === 0
        ? undefined
        : { titles: validTitleRows.map((row) => ({ titles: (row.titles ?? []).map((tag) => tag.text) })) },
    // ⚠️ `locationCriteria`, NOT `location` — and only when `jobCompanyInfoEnabled` is off,
    // which is the tenant under test and every reference capture.
    // ⚠️ ONE KEY OR THE OTHER, NEVER BOTH — see the loader's note. With the flag on the rail
    // reads "Company location" and the wire carries `companyLocationCriteria`; with it off it
    // is `locationCriteria`. Sending the wrong one saves a location the server never applies.
    locationCriteria: jobCompanyInfoEnabled
      ? undefined
      : (mapLocation(form.location, regions) as WireLocationCriteria | undefined),
    companyLocationCriteria: jobCompanyInfoEnabled
      ? (mapLocation(form.location, regions) as WireLocationCriteria | undefined)
      : undefined,
    // `mapSizeCriteria` / `mapTypeCriteria` — flag-gated and dropped when empty.
    sizeCriteria:
      !jobCompanyInfoEnabled || form.companySizes.length === 0
        ? undefined
        : { sizes: form.companySizes.map((tag) => tag.text) },
    typeCriteria:
      !jobCompanyInfoEnabled || form.companyTypes.length === 0
        ? undefined
        : { types: form.companyTypes.map((tag) => tag.text) },
    // ⚠️ `mapSupplyCriteria` NEEDS THE HIRING BOX TOO. It returns early unless
    // `hiringLikelihoodCriteria.highLikelihood` is set, which is the same nesting the markup
    // has — the supply checkbox only exists inside `.high-hiring-likelihood-options`.
    supplyCriteria:
      !jobCompanyInfoEnabled || !form.highHiringLikelihood || !form.highSupply
        ? undefined
        : { highSupply: true },
    // The other three in the same block, each with the same two-part guard as supply:
    // `mapCategoriesCriteria`, `mapOpenJobsCriteria`, `mapLastOpenedJobCriteria`.
    categoriesCriteria:
      !jobCompanyInfoEnabled || !form.highHiringLikelihood || form.hiringCategories.length === 0
        ? undefined
        : { categories: form.hiringCategories.map((tag) => tag.text) },
    openJobsCriteria:
      !jobCompanyInfoEnabled || !form.highHiringLikelihood || form.openJobsMin === undefined
        ? undefined
        : { min: form.openJobsMin },
    lastOpenedJobCriteria:
      !jobCompanyInfoEnabled || !form.highHiringLikelihood || form.lastOpenedJobDaysAgo === undefined
        ? undefined
        : { daysAgo: form.lastOpenedJobDaysAgo },
    // ⚠️ THE CRITERION IS THE CHECKBOX, AND THE TWO YEARS MAY BOTH BE NULL INSIDE IT.
    // `mapSwitchedCompaniesCriteria` returns early on `!enabled` and otherwise sends `{min, max}`
    // whatever they hold, so an empty object here is meaningful rather than droppable — which
    // is why it is `{}` and not `undefined` when the box is ticked with no years.
    switchedCompanyCriteria: !form.switchedCompany
      ? undefined
      : {
          min: form.switchedCompanyMin === '' ? null : Number(form.switchedCompanyMin),
          max: form.switchedCompanyMax === '' ? null : Number(form.switchedCompanyMax),
        },
    companyNamesCriteria:
      form.companyNames.length === 0 ? undefined : { companyNames: form.companyNames.map((tag) => tag.text) },
    industryCriteria:
      form.industries.length === 0 ? undefined : { industries: form.industries.map((tag) => tag.text) },
    hiringLikelihoodCriteria: form.highHiringLikelihood ? { highLikelihood: true } : undefined,
    highestActivityLevelCriteria:
      form.previousEngagement.length === 0
        ? undefined
        : { values: form.previousEngagement.map((tag) => ({ ...(tag.source ?? { text: tag.text }) })) },
    keywordsCriteria: mapKeywords(form.keywords),
    atsFilters: validAtsFilters,
    ...(form.versionId === undefined ? {} : { versionId: form.versionId }),
  }

  for (const key of Object.keys(mapped)) {
    if (isEmptyCriterion(mapped[key])) delete mapped[key]
  }

  // The unmodelled keys ride through untouched. Only keys this form does not own are carried:
  // one it DOES own and has emptied must stay deleted, or clearing a filter would never take.
  const owned = ownedKeys(jobCompanyInfoEnabled)
  for (const [key, value] of Object.entries(saved ?? {})) {
    if (!owned.has(key) && !isEmptyCriterion(value)) mapped[key] = value
  }
  return mapped
}

/**
 * ⚠️ OWNERSHIP FOLLOWS WHAT THE FORM CAN DRAW, WHICH THE FLAG DECIDES. The four company
 * criteria exist only with `jobCompanyInfoEnabled`; on a tenant without it this form neither
 * loads nor renders them, so it must not be allowed to delete them either — the carry-through
 * is the only thing standing between a rename and silent data loss. Upstream rebuilds its
 * whole object each save and would drop them; this is deliberately the safer of the two, and
 * it costs nothing on a tenant that can draw them, where they are owned as normal.
 */
function ownedKeys(jobCompanyInfoEnabled: boolean): Set<string> {
  return jobCompanyInfoEnabled
    ? new Set([
        ...OWNED_KEYS,
        'companyLocationCriteria',
        'sizeCriteria',
        'typeCriteria',
        'supplyCriteria',
        'categoriesCriteria',
        'openJobsCriteria',
        'lastOpenedJobCriteria',
      ])
    : OWNED_KEYS
}

/** Every criterion this form can draw — and therefore every one it may delete. */
const OWNED_KEYS = new Set([
  'titleCriteria',
  'locationCriteria',
  'switchedCompanyCriteria',
  'companyNamesCriteria',
  'industryCriteria',
  'hiringLikelihoodCriteria',
  'highestActivityLevelCriteria',
  'keywordsCriteria',
  'atsFilters',
  'versionId',
])
